import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { AppButton } from '@/components'
import { useSavingsStore } from '@/store/useSavingsStore'
import { useUserStore } from '@/store/useUserStore'
import { useGetBlockchainsCatalogs } from '@/domain/blockchains/useCases/useGetBlockchainsCatalogs'
import { useGetAboutCurrencyInBlockchain } from '@/domain/currencies/useCases/useGetAboutCurrencyInBlockchain'
import { useValidateFiatAmount } from '@/hooks/useValidateFiatAmount'
import { currencyUtils } from '@/utils'

const { parseAmountToFloat } = currencyUtils

export const FirstDepositButtonConfirmation: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const fiatAmount = useSavingsStore((state) => state.goalTransferIn.fiatAmount)
  const cryptoAmount = useSavingsStore(
    (state) => state.goalTransferIn.cryptoAmount,
  )
  const setGoalTransferIn = useSavingsStore((state) => state.setGoalTransferIn)

  const user = useUserStore((s) => s.user)
  const displayCurrency = user?.display_currency || 'USD'

  // Получаем список блокчейнов для USDT
  const { data: blockchains } = useGetBlockchainsCatalogs({
    currency_isin: 'USDT',
  })

  // Получаем информацию о валюте в блокчейне для валидации минимальных сумм
  const {
    data: currencyInBlockchainInfos,
    isLoading: isLoadingCurrencyInBlockchainInfos,
  } = useGetAboutCurrencyInBlockchain({
    currency_isin: 'USDT',
    blockchain_code: blockchains?.blockchains[0]?.code ?? '',
  })

  // Хук валидации суммы
  const { validateFiatAmount } = useValidateFiatAmount({
    cryptoCode: 'USDT',
    min: {
      amount: String(currencyInBlockchainInfos?.min_amount_fiat ?? 1),
      code: displayCurrency,
    },
    max: {
      amount: String(1000000), // Большой лимит для депозита
      code: displayCurrency,
    },
    customMaxMessage: 'validationErrors.maxDepositExceeded',
    onInvalidAmount: () => {
      setGoalTransferIn({
        triggerShake: true,
        fiatAmount: {
          amount: '0',
          code: displayCurrency,
        },
        cryptoAmount: {
          amount: '0',
          code: 'USDT',
        },
      })
    },
  })

  const redirectToConfirmTransfer = () => {
    setTimeout(() => navigate('/savings/transfer-in/confirm-transfer'), 200)
  }

  const handleDeposit = async () => {
    // Проверяем, что данные о валюте в блокчейне загружены
    if (isLoadingCurrencyInBlockchainInfos) {
      return
    }

    // Валидация суммы
    const isValid = validateFiatAmount({
      fiatAmount,
    })

    if (!isValid) {
      return
    }

    const blockchainsList = blockchains?.blockchains || []

    // Если есть только один блокчейн, сразу переходим к подтверждению
    if (blockchainsList.length === 1) {
      redirectToConfirmTransfer()
      return
    }

    // Если блокчейнов несколько, открываем модал выбора
    // TODO: открыть модал выбора блокчейна
    redirectToConfirmTransfer()
  }

  // Проверяем, можно ли нажать кнопку
  const fiatAmountValue = parseAmountToFloat({
    amount: fiatAmount.amount,
    decimalSeparator: '.',
  })

  const minAmount = currencyInBlockchainInfos?.min_amount_fiat ?? 1
  const isDisabled =
    fiatAmountValue < minAmount || isLoadingCurrencyInBlockchainInfos

  return (
    <AppButton
      label={t('form:actions.confirm')}
      onPress={handleDeposit}
      isDisabled={isDisabled}
      isLoading={isLoadingCurrencyInBlockchainInfos}
    />
  )
}
